import { motion } from 'framer-motion';
import {
  BellOff,
  Check,
  ChevronLeft,
  ChevronRight,
  ListChecks,
  Loader2,
  RefreshCcw,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';

import {
  RAINMAKER_EVENTS,
  RAINMAKER_PROPERTIES,
} from '@/constants/analytics/rainmaker-events';
import { usePostHogCapture } from '@/hooks/usePostHogCapture';
import { cn } from '@/lib/utils';
import {
  RainmakerStatus,
  RainmakerSuggestion,
  RainmakerSuggestionStatus,
} from '@/services/types/rainmaker-types';

import { EvidenceModal } from './EvidenceModal';
import { ExecutionModal } from './ExecutionModal';
import { RainmakerLogo } from './RainmakerLogo';
import { getEvidenceIcon } from './utils';

interface RainmakerCardProps {
  status: RainmakerStatus;
  suggestions: RainmakerSuggestion[];
  matterId: string;
  onExecute?: (suggestionId: string) => Promise<void>;
  onDismiss?: (suggestionId: string) => Promise<void>;
  onRestore?: () => Promise<void>;
  className?: string;
}

export const RainmakerCard: React.FC<RainmakerCardProps> = ({
  status,
  suggestions,
  matterId,
  onExecute,
  onDismiss,
  onRestore,
  className,
}) => {
  const capture = usePostHogCapture();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showExecutionModal, setShowExecutionModal] = useState(false);
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);

  const suggestion = suggestions[currentIndex];
  const hasMultiple = suggestions.length > 1;

  // Track suggestion viewed when suggestion changes
  useEffect(() => {
    if (suggestion && status === RainmakerStatus.ACTIVE) {
      capture(RAINMAKER_EVENTS.SUGGESTION_VIEWED, {
        [RAINMAKER_PROPERTIES.PAGE]: 'task_detail',
        [RAINMAKER_PROPERTIES.COMPONENT]: 'rainmaker_card',
        [RAINMAKER_PROPERTIES.MATTER_ID]: matterId,
        [RAINMAKER_PROPERTIES.SUGGESTION_ID]: suggestion.id,
        [RAINMAKER_PROPERTIES.SUGGESTION_TITLE]: suggestion.title,
        [RAINMAKER_PROPERTIES.SUGGESTION_STATUS]: suggestion.status,
        [RAINMAKER_PROPERTIES.ACTION_COUNT]: suggestion.actions?.length ?? 0,
        [RAINMAKER_PROPERTIES.EVIDENCE_COUNT]: suggestion.evidence?.length ?? 0,
        [RAINMAKER_PROPERTIES.CURRENT_INDEX]: currentIndex,
        [RAINMAKER_PROPERTIES.TOTAL_SUGGESTIONS]: suggestions.length,
      });
    }
  }, [suggestion?.id, status, currentIndex]);

  // Track completed state viewed
  useEffect(() => {
    if (status === RainmakerStatus.DONE) {
      const executedCount = suggestions.filter(
        (s) => s.status === RainmakerSuggestionStatus.EXECUTED,
      ).length;
      const totalActions = suggestions
        .filter((s) => s.status === RainmakerSuggestionStatus.EXECUTED)
        .reduce((sum, s) => sum + (s.actions?.length ?? 0), 0);
      capture(RAINMAKER_EVENTS.COMPLETED_STATE_VIEWED, {
        [RAINMAKER_PROPERTIES.PAGE]: 'task_detail',
        [RAINMAKER_PROPERTIES.COMPONENT]: 'rainmaker_card',
        [RAINMAKER_PROPERTIES.MATTER_ID]: matterId,
        [RAINMAKER_PROPERTIES.STATUS]: 'done',
        [RAINMAKER_PROPERTIES.EXECUTED_SUGGESTION_COUNT]: executedCount,
        [RAINMAKER_PROPERTIES.ACTION_COUNT]: totalActions,
        [RAINMAKER_PROPERTIES.TOTAL_SUGGESTIONS]: suggestions.length,
      });
    }
  }, [status]);

  const handlePrev = () => {
    if (currentIndex > 0) {
      capture(RAINMAKER_EVENTS.SUGGESTION_NAVIGATED, {
        [RAINMAKER_PROPERTIES.PAGE]: 'task_detail',
        [RAINMAKER_PROPERTIES.COMPONENT]: 'rainmaker_card',
        [RAINMAKER_PROPERTIES.MATTER_ID]: matterId,
        [RAINMAKER_PROPERTIES.DIRECTION]: 'prev',
        [RAINMAKER_PROPERTIES.CURRENT_INDEX]: currentIndex - 1,
        [RAINMAKER_PROPERTIES.TOTAL_SUGGESTIONS]: suggestions.length,
      });
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < suggestions.length - 1) {
      capture(RAINMAKER_EVENTS.SUGGESTION_NAVIGATED, {
        [RAINMAKER_PROPERTIES.PAGE]: 'task_detail',
        [RAINMAKER_PROPERTIES.COMPONENT]: 'rainmaker_card',
        [RAINMAKER_PROPERTIES.MATTER_ID]: matterId,
        [RAINMAKER_PROPERTIES.DIRECTION]: 'next',
        [RAINMAKER_PROPERTIES.CURRENT_INDEX]: currentIndex + 1,
        [RAINMAKER_PROPERTIES.TOTAL_SUGGESTIONS]: suggestions.length,
      });
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleExecute = async () => {
    if (!onExecute) return;
    setIsExecuting(true);
    try {
      await onExecute(suggestion.id);
      capture(RAINMAKER_EVENTS.SUGGESTION_EXECUTED, {
        [RAINMAKER_PROPERTIES.PAGE]: 'task_detail',
        [RAINMAKER_PROPERTIES.COMPONENT]: 'rainmaker_card',
        [RAINMAKER_PROPERTIES.MATTER_ID]: matterId,
        [RAINMAKER_PROPERTIES.SUGGESTION_ID]: suggestion.id,
        [RAINMAKER_PROPERTIES.SUGGESTION_TITLE]: suggestion.title,
        [RAINMAKER_PROPERTIES.ACTION_COUNT]: suggestion.actions?.length ?? 0,
      });
      setShowExecutionModal(false);
    } finally {
      setIsExecuting(false);
    }
  };

  const handleDismiss = async () => {
    if (!onDismiss) return;
    setIsDismissing(true);
    try {
      await onDismiss(suggestion.id);
      capture(RAINMAKER_EVENTS.SUGGESTION_DISMISSED, {
        [RAINMAKER_PROPERTIES.PAGE]: 'task_detail',
        [RAINMAKER_PROPERTIES.COMPONENT]: 'rainmaker_card',
        [RAINMAKER_PROPERTIES.MATTER_ID]: matterId,
        [RAINMAKER_PROPERTIES.SUGGESTION_ID]: suggestion.id,
        [RAINMAKER_PROPERTIES.SUGGESTION_TITLE]: suggestion.title,
      });

      // After dismissing, move to the next non-dismissed suggestion
      // Find the next pending suggestion
      const nextPendingIndex = suggestions.findIndex(
        (s, idx) =>
          idx > currentIndex && s.status === RainmakerSuggestionStatus.PENDING,
      );

      if (nextPendingIndex !== -1) {
        // Move to the next pending suggestion
        setCurrentIndex(nextPendingIndex);
      } else {
        // No more pending suggestions after current, check before
        const previousPendingIndex = suggestions.findIndex(
          (s, idx) =>
            idx < currentIndex &&
            s.status === RainmakerSuggestionStatus.PENDING,
        );

        if (previousPendingIndex !== -1) {
          setCurrentIndex(previousPendingIndex);
        } else if (currentIndex > 0) {
          // No pending suggestions, just move back if possible
          setCurrentIndex(currentIndex - 1);
        }
      }
    } finally {
      setIsDismissing(false);
    }
  };

  const handleRestore = async () => {
    if (!onRestore) return;
    setIsRestoring(true);
    try {
      await onRestore();
      const dismissedCount = suggestions.filter(
        (s) => s.status === RainmakerSuggestionStatus.DISMISSED,
      ).length;
      capture(RAINMAKER_EVENTS.SUGGESTIONS_RESTORED, {
        [RAINMAKER_PROPERTIES.PAGE]: 'task_detail',
        [RAINMAKER_PROPERTIES.COMPONENT]: 'rainmaker_card',
        [RAINMAKER_PROPERTIES.MATTER_ID]: matterId,
        [RAINMAKER_PROPERTIES.DISMISSED_SUGGESTION_COUNT]: dismissedCount,
        [RAINMAKER_PROPERTIES.TOTAL_SUGGESTIONS]: suggestions.length,
      });
    } finally {
      setIsRestoring(false);
    }
  };

  const handleOpenExecutionModal = () => {
    capture(RAINMAKER_EVENTS.EXECUTION_MODAL_OPENED, {
      [RAINMAKER_PROPERTIES.PAGE]: 'task_detail',
      [RAINMAKER_PROPERTIES.COMPONENT]: 'rainmaker_card',
      [RAINMAKER_PROPERTIES.MATTER_ID]: matterId,
      [RAINMAKER_PROPERTIES.SUGGESTION_ID]: suggestion.id,
      [RAINMAKER_PROPERTIES.SUGGESTION_TITLE]: suggestion.title,
      [RAINMAKER_PROPERTIES.ACTION_COUNT]: suggestion.actions?.length ?? 0,
    });
    setShowExecutionModal(true);
  };

  const handleOpenEvidenceModal = () => {
    const evidenceTypes = suggestion.evidence?.map((e) => e.type) ?? [];
    capture(RAINMAKER_EVENTS.EVIDENCE_VIEWED, {
      [RAINMAKER_PROPERTIES.PAGE]: 'task_detail',
      [RAINMAKER_PROPERTIES.COMPONENT]: 'rainmaker_card',
      [RAINMAKER_PROPERTIES.MATTER_ID]: matterId,
      [RAINMAKER_PROPERTIES.SUGGESTION_ID]: suggestion.id,
      [RAINMAKER_PROPERTIES.EVIDENCE_COUNT]: suggestion.evidence?.length ?? 0,
      [RAINMAKER_PROPERTIES.EVIDENCE_TYPES]: evidenceTypes.join(','),
    });
    setShowEvidenceModal(true);
  };

  const isPending = suggestion.status === RainmakerSuggestionStatus.PENDING;
  const isExecuted = suggestion.status === RainmakerSuggestionStatus.EXECUTED;
  const isDismissed = suggestion.status === RainmakerSuggestionStatus.DISMISSED;

  const executedSuggestions = suggestions.filter(
    (s) => s.status === RainmakerSuggestionStatus.EXECUTED,
  );

  // DONE STATE: Show execution report
  if (status === RainmakerStatus.DONE) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className={cn(
          'relative overflow-hidden rounded-xl border border-emerald-500/20 bg-[#0F172A] shadow-2xl',
          className,
        )}
      >
        {/* Success Header */}
        <div className="relative flex h-8 items-center justify-between border-b border-white/5 bg-white/5 px-2">
          <div className="flex items-center gap-2">
            <div className="flex h-3 w-3 items-center justify-center rounded-full bg-emerald-500/20">
              <ListChecks size={12} className="text-emerald-400" />
            </div>
            <span className="text-[10px] font-bold tracking-wide text-emerald-100">
              RAINMAKER COMPLETE
            </span>
          </div>
          <div className="font-mono text-[10px] text-emerald-500/60">
            ID: {Date.now().toString().slice(-8)}
          </div>
        </div>

        {/* Content */}
        <div className="relative p-2">
          <div className="mb-2 grid grid-cols-1 gap-2">
            {executedSuggestions.map((s) =>
              s.actions.map((action, idx) => (
                <div key={`${s.id}-${idx}`} className="flex items-center gap-2">
                  <div className="flex h-3 w-3 shrink-0 items-center justify-center rounded-full bg-emerald-500">
                    <Check size={10} className="text-white" strokeWidth={3} />
                  </div>
                  <span className="text-[10px] text-slate-300">
                    {action.description}
                  </span>
                </div>
              )),
            )}
          </div>

          <div className="flex items-center justify-between border-t border-white/5 pt-1 font-mono text-[8px] text-slate-500">
            <span>System Status: Nominal</span>
            <span>Changes persisted to database.</span>
          </div>

          {/* The Digital Stamp */}
          <div className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none opacity-20 select-none mix-blend-screen">
            <RainmakerLogo
              size={96}
              className="text-emerald-500 rotate-[-12deg]"
            />
          </div>
        </div>
      </motion.div>
    );
  }

  // IGNORED STATE: Show collapsed dismissed state
  if (status === RainmakerStatus.IGNORED) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.15 }}
        className={cn(
          'flex items-center justify-between p-2 bg-slate-50 rounded-lg border border-dashed border-slate-300 animate-in fade-in duration-500',
          className,
        )}
      >
        {/* ALL DISMISSED STATE - COMPACT */}

        <div className="flex items-center gap-3">
          <div className="w-6 h-6 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-400 shadow-sm">
            <BellOff size={12} />
          </div>
          <span className="text-xs text-slate-500 font-medium">
            Rainmaker suggestions dismissed
          </span>
        </div>
        <button
          type="button"
          onClick={handleRestore}
          disabled={isRestoring}
          className="flex items-center gap-1.5 text-[10px] font-semibold text-slate-600 hover:text-indigo-600 bg-white hover:bg-indigo-50 border border-slate-200 hover:border-indigo-100 px-2.5 py-1 rounded transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isRestoring ? (
            <Loader2 size={10} className="animate-spin" />
          ) : (
            <RefreshCcw size={10} />
          )}
          Restore
        </button>
      </motion.div>
    );
  }

  // ACTIVE STATE: Show normal suggestion UI
  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.15 }}
        className={cn(
          'relative overflow-hidden rounded-md bg-white shadow-sm border border-black',
          isExecuted && 'ring-1 ring-green-200 bg-green-50/30',
          isDismissed && 'ring-1 ring-gray-200 bg-gray-50/30 opacity-50',
          className,
        )}
      >
        {/* Header - Dark with Rainmaker branding and animated effects */}
        <div className="relative overflow-hidden bg-gradient-to-r from-gray-900 to-gray-800 px-2.5 py-1.5 border-b border-violet-500/30">
          {/* Animated shimmer sweep */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
            animate={{ x: ['-100%', '100%'] }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
              repeatDelay: 1,
            }}
          />
          {/* Pulsing violet glow */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-violet-500/10 via-transparent to-violet-500/10"
            animate={{ opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          />
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <RainmakerLogo size={12} className="text-violet-400" />
              <span className="text-[10px] font-semibold uppercase tracking-wide text-white">
                Rainmaker
              </span>
              <span className="rounded bg-violet-500/20 px-1 py-0.5 text-[9px] font-medium text-violet-300">
                Active
              </span>
            </div>
            {hasMultiple && (
              <div className="flex items-center gap-1.5">
                <span className="text-[9px] tabular-nums font-medium text-gray-300">
                  {currentIndex + 1}/{suggestions.length}
                </span>
                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={handlePrev}
                    disabled={currentIndex === 0}
                    className="rounded p-0.5 hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent"
                    aria-label="Previous suggestion"
                  >
                    <ChevronLeft className="h-3 w-3 text-white" />
                  </button>
                  <button
                    type="button"
                    onClick={handleNext}
                    disabled={currentIndex === suggestions.length - 1}
                    className="rounded p-0.5 hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent"
                    aria-label="Next suggestion"
                  >
                    <ChevronRight className="h-3 w-3 text-white" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="px-2.5 py-2">
          {/* Title & Description */}
          <div className="mb-2">
            <div className="flex items-start justify-between gap-2">
              <h3 className="text-xs font-semibold leading-snug text-gray-900">
                {suggestion.title}
              </h3>
              {!isPending && (
                <div
                  className={cn(
                    'flex shrink-0 items-center gap-0.5 rounded px-1.5 py-0.5 text-[9px] font-medium',
                    isExecuted && 'bg-green-100 text-green-700',
                    isDismissed && 'bg-gray-100 text-gray-500',
                  )}
                >
                  {isExecuted && <Check className="h-2.5 w-2.5" />}
                  {isExecuted ? 'Done' : 'Dismissed'}
                </div>
              )}
            </div>
            {suggestion.description && (
              <p className="text-[9px] leading-relaxed text-gray-700 font-semibold">
                {suggestion.description}
              </p>
            )}
          </div>

          {/* Stacked Evidence Cards (Bottom Right) */}
          {suggestion.evidence && suggestion.evidence.length > 0 && (
            <button
              type="button"
              className="group/stack absolute -bottom-2 right-2 cursor-pointer"
              onClick={handleOpenEvidenceModal}
              aria-label="View evidence details"
            >
              <div className="relative h-12 w-10">
                {/* Render up to 3 stacked cards */}
                {suggestion.evidence.slice(0, 3).map((evidence, idx) => {
                  let rotation = 'rotate-0';
                  if (idx === 0) rotation = 'rotate-[6deg]';
                  if (idx === 1) rotation = '-rotate-[3deg]';
                  const zIndex = 2 - idx;

                  return (
                    <div
                      key={idx}
                      className={cn(
                        'absolute inset-0 flex items-center justify-center rounded-md border border-slate-300 bg-white shadow-sm transition-transform duration-300 ease-out',
                        rotation,
                        idx === 0 &&
                          'group-hover/stack:-translate-y-2 group-hover/stack:translate-x-2',
                        idx === 1 &&
                          'group-hover/stack:-translate-x-2 group-hover/stack:-translate-y-1',
                      )}
                      style={{ zIndex }}
                    >
                      <span className="text-violet-500">
                        {getEvidenceIcon(evidence.type)}
                      </span>
                    </div>
                  );
                })}

                {/* Badge showing count */}
                {suggestion.evidence.length > 1 && (
                  <div className="absolute -right-2 -top-2 z-4 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white bg-violet-600 text-[10px] font-bold text-white shadow-sm">
                    {suggestion.evidence.length}
                  </div>
                )}
              </div>

              {/* Hover tooltip */}
              <div className="absolute right-0 top-full mt-2 w-max rounded bg-violet-50 px-2 py-0.5 text-[10px] font-medium text-violet-600 opacity-0 transition-opacity group-hover/stack:opacity-100">
                Click to Review
              </div>
            </button>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2">
            {isPending && (
              <>
                <button
                  type="button"
                  onClick={handleOpenExecutionModal}
                  disabled={isExecuting || isDismissing}
                  className="inline-flex items-center gap-1 rounded bg-gray-900 px-2.5 py-1 text-[10px] font-medium text-white transition-colors hover:bg-gray-800 disabled:opacity-50"
                >
                  {isExecuting ? (
                    <>
                      <Loader2 className="h-2.5 w-2.5 animate-spin" />
                      <span>Executing</span>
                    </>
                  ) : (
                    suggestion.cta_label
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleDismiss}
                  disabled={isExecuting || isDismissing}
                  className="text-[10px] font-medium text-gray-400 transition-colors hover:text-gray-600 disabled:opacity-50"
                >
                  {isDismissing ? (
                    <Loader2 className="h-2.5 w-2.5 animate-spin" />
                  ) : (
                    'Dismiss'
                  )}
                </button>
              </>
            )}
            {!isPending && (
              <div className="text-[9px] text-gray-400">
                {suggestion.acted_at &&
                  `${isExecuted ? 'Executed' : 'Dismissed'} ${formatDistanceToNow(new Date(suggestion.acted_at))}`}
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Execution Modal */}
      {showExecutionModal && (
        <ExecutionModal
          suggestion={suggestion}
          onClose={() => setShowExecutionModal(false)}
          onExecute={handleExecute}
          isExecuting={isExecuting}
        />
      )}

      {/* Evidence Modal */}
      {showEvidenceModal && (
        <EvidenceModal
          suggestion={suggestion}
          matterId={matterId}
          onClose={() => setShowEvidenceModal(false)}
          onExecute={handleExecute}
          isExecuting={isExecuting}
        />
      )}
    </>
  );
};

function formatDistanceToNow(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}
